import React, { useEffect, useState } from 'react';

import { useGetEvent, useGetSample, useGetFaceAttributeSamples } from '@vlabs/api/artemy/client';
import type { FaceId } from '@vlabs/api/artemy/model';
import type { Uuid } from '@vlabs/api/artemy/model/Uuid';
import { ZoomInIcon, DownloadIcon } from '@vlabs/icons';
import type { FaceSampleProps } from '@vlabs/uikit';
import { FaceSample, ButtonScrimGroup } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button';

import { useFullscreenImageViewer } from '@vlabs/monokit/features/view-photo';
import { downloadImage } from '@vlabs/monokit/shared/lib/images';
import { ImageSamplePlaceholder } from '@vlabs/monokit/shared/ui/image-sample-placeholder/ImageSamplePlaceholder';

import { buildTestid } from '../../../../shared/lib/utils';

interface EventFaceTopMatchSamplePoint3Props extends Omit<FaceSampleProps, 'src' | 'buttonOnBottom'> {
  /** для топ-матча с лицом берем сэмпл по самому лицу из топ-матча */
  faceId?: FaceId;
  /** для топ-матча с событием берем сэмпл из детекции этого события из топ-матча */
  matchedEventId?: Uuid;
}

const displayName = 'EventFaceTopMatchSamplePoint3';
const testid = buildTestid(displayName);

function EventFaceTopMatchSamplePoint3({
  faceId = undefined,
  matchedEventId = undefined,
  'data-testid': dataTestId = undefined,
  ...props
}: EventFaceTopMatchSamplePoint3Props): React.JSX.Element {
  // у события нужен именно detection sample
  const { data: topMatchEvent } = useGetEvent(
    matchedEventId ?? '',
    { target: ['face_detections'] },
    { query: { enabled: !!matchedEventId && !faceId } },
  );

  // у лица берем первый актуальный attribute sample
  const { data: faceAttributeSamplesId } = useGetFaceAttributeSamples(
    faceId ?? '',
    { query: { enabled: !!faceId && !matchedEventId } },
  );

  const sampleId = topMatchEvent?.face_detections?.find(({ sample_id }) => !!sample_id)?.sample_id
    ?? faceAttributeSamplesId?.samples?.[0];

  const { data: sample } = useGetSample('faces', sampleId ?? '', {
    query: { enabled: !!sampleId },
  });

  const [src, setSrc] = useState<string>();

  useEffect(() => {
    if (!sample) {
      setSrc(undefined);
      return undefined;
    }

    const objectUrl = URL.createObjectURL(sample);
    setSrc(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [sample]);

  const fullscreenImageViewer = useFullscreenImageViewer({ url: src });

  return (
    <FaceSample
      buttonOnBottom={(
        <ButtonScrimGroup size="small">
          {/* //FIXME: Заменить на ViewPhotoIconButton и DownloadImageIconButton когда ButtonScrimGroup будет поддерживать */}
          <IconButton
            disabled={!src}
            icon={<ZoomInIcon />}
            kind="secondary"
            onClick={fullscreenImageViewer}
            size="small"
            variant="flat"
            {...testid('ZoomButton', dataTestId)}
          />
          <IconButton
            disabled={!src}
            icon={<DownloadIcon />}
            kind="secondary"
            onClick={() => downloadImage(src)}
            size="small"
            variant="flat"
            {...testid('DownloadButton', dataTestId)}
          />
        </ButtonScrimGroup>
      )}
      placeholder={<ImageSamplePlaceholder />}
      size="large"
      src={src}
      {...testid(dataTestId)}
      {...props}
    />
  );
}

EventFaceTopMatchSamplePoint3.displayName = displayName;

export { EventFaceTopMatchSamplePoint3 };
